/**
 * The "Conditions" section of an alert rule: what the AI event must have REPORTED for the
 * rule to fire, how long that must hold, and what the alert says.
 *
 *   value < 150  and  low_for_s ≥ 300                 (panel OCR)
 *   state = no_belt_movement, held 1m                 (belt stopped)
 *
 * Field suggestions come from the command-center's event catalog — the fields the AI module
 * has actually sent for the rule's event types, with their observed ranges — so a new model
 * needs no UI change. Any field can still be typed by hand.
 *
 * Same component in the local and the cloud Alert Rules page.
 */
import { useEffect, useMemo, useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { aaInputCls, aaLabelCls } from '../alertActions/AlertActionsControls';
import {
  BUILTIN_FIELDS,
  OP_OPTIONS,
  coerceValue,
  fieldsFor,
  formatSeconds,
  opKind,
  parseConditions,
  type CatalogField,
  type EventCatalogEntry,
  type RuleCondition,
  type RuleConditions,
} from './ruleConditionsModel';

export interface RuleConditionsValue {
  event_type?: string;
  conditions?: unknown;
  sustain_seconds?: number | string | null;
  message_template?: string | null;
}

export interface RuleConditionsFieldsProps {
  value: RuleConditionsValue;
  onChange: (patch: Partial<RuleConditionsValue>) => void;
  /** GET /config/event-catalog. Empty is fine: fields can be typed. */
  catalog: EventCatalogEntry[];
  disabled?: boolean;
  /** The host edits the alert message itself (the rule form's own message section). */
  hideMessage?: boolean;
}

const parseTypes = (v?: string) => String(v ?? '').split(',').map((t) => t.trim()).filter(Boolean);

function fieldHint(f?: CatalogField): string {
  if (!f) return '';
  if (f.type === 'number' && f.min !== undefined && Number.isFinite(f.min)) {
    return f.min === f.max ? `seen: ${f.min}` : `seen: ${f.min} – ${f.max}`;
  }
  if (f.samples?.length) return `seen: ${f.samples.slice(0, 4).join(', ')}`;
  return '';
}

/** One condition row. Keeps the typed text locally so "1," or "-" can be typed mid-edit. */
function ConditionRow({
  rule, index, fields, listId, disabled, onChange, onRemove,
}: {
  rule: RuleCondition;
  index: number;
  fields: Record<string, CatalogField>;
  listId: string;
  disabled?: boolean;
  onChange: (next: RuleCondition) => void;
  onRemove: () => void;
}) {
  const kind = opKind(rule.op);
  const toText = (v: unknown) => (Array.isArray(v) ? v.join(', ') : v === undefined || v === null ? '' : String(v));
  const [text, setText] = useState(() => toText(rule.value));
  const [low, setLow] = useState(() => (Array.isArray(rule.value) ? String(rule.value[0] ?? '') : ''));
  const [high, setHigh] = useState(() => (Array.isArray(rule.value) ? String(rule.value[1] ?? '') : ''));

  // A different rule loaded into the same row position.
  useEffect(() => {
    setText(toText(rule.value));
    setLow(Array.isArray(rule.value) ? String(rule.value[0] ?? '') : '');
    setHigh(Array.isArray(rule.value) ? String(rule.value[1] ?? '') : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, rule.op]);

  const field = fields[rule.field];
  const samplesId = `${listId}-samples-${index}`;

  const setOp = (op: string) => {
    const k = opKind(op);
    const value = k === 'none' ? undefined
      : k === 'range' ? coerceValue(op, [low, high])
        : coerceValue(op, text);
    onChange({ ...rule, op: op as RuleCondition['op'], value });
  };

  return (
    <div className="flex flex-wrap items-start gap-1.5">
      <div className="min-w-[9rem] flex-[2]">
        <input
          list={listId}
          value={rule.field}
          disabled={disabled}
          onChange={(e) => onChange({ ...rule, field: e.target.value.trim() })}
          className={`${aaInputCls} mt-0`}
          placeholder="field, e.g. value"
          aria-label={`Condition ${index + 1} field`}
        />
        {fieldHint(field) && <div className="mt-0.5 truncate text-[10px] text-muted-foreground">{fieldHint(field)}</div>}
      </div>
      <select
        value={rule.op}
        disabled={disabled}
        onChange={(e) => setOp(e.target.value)}
        className={`${aaInputCls} mt-0 w-auto min-w-[6.5rem] flex-none`}
        aria-label={`Condition ${index + 1} operator`}
      >
        {OP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <div className="min-w-[8rem] flex-[2]">
        {kind === 'range' ? (
          <div className="flex items-center gap-1">
            <input value={low} disabled={disabled} inputMode="decimal" placeholder="low"
              onChange={(e) => { setLow(e.target.value); onChange({ ...rule, value: coerceValue(rule.op, [e.target.value, high]) }); }}
              className={`${aaInputCls} mt-0`} aria-label={`Condition ${index + 1} low`} />
            <span className="text-[11px] text-muted-foreground">to</span>
            <input value={high} disabled={disabled} inputMode="decimal" placeholder="high"
              onChange={(e) => { setHigh(e.target.value); onChange({ ...rule, value: coerceValue(rule.op, [low, e.target.value]) }); }}
              className={`${aaInputCls} mt-0`} aria-label={`Condition ${index + 1} high`} />
          </div>
        ) : kind === 'none' ? (
          <div className="py-1.5 text-[12px] text-muted-foreground">—</div>
        ) : (
          <>
            <input
              value={text}
              disabled={disabled}
              list={field?.samples?.length ? samplesId : undefined}
              inputMode={kind === 'number' ? 'decimal' : undefined}
              placeholder={kind === 'list' ? 'a, b, c' : kind === 'number' ? 'number' : 'value'}
              onChange={(e) => { setText(e.target.value); onChange({ ...rule, value: coerceValue(rule.op, e.target.value) }); }}
              className={`${aaInputCls} mt-0`}
              aria-label={`Condition ${index + 1} value`}
            />
            {field?.samples?.length ? (
              <datalist id={samplesId}>{field.samples.map((s) => <option key={s} value={s} />)}</datalist>
            ) : null}
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="mt-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-400 disabled:opacity-50"
        aria-label={`Remove condition ${index + 1}`}
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function RuleConditionsFields({ value, onChange, catalog, disabled, hideMessage }: RuleConditionsFieldsProps) {
  const conditions: RuleConditions = parseConditions(value.conditions) || { match: 'all', rules: [] };
  const fields = useMemo(
    () => ({ ...fieldsFor(catalog, parseTypes(value.event_type)), ...BUILTIN_FIELDS }),
    [catalog, value.event_type],
  );
  const listId = useMemo(() => `rule-fields-${Math.random().toString(36).slice(2, 8)}`, []);

  const setRules = (rules: RuleCondition[]) =>
    onChange({ conditions: rules.length ? { ...conditions, rules } : null });
  const firstNumeric = Object.entries(fields).find(([, f]) => f.type === 'number')?.[0] || '';
  const hold = Number(value.sustain_seconds) || 0;
  const fieldNames = Object.keys(fields).filter((f) => !(f in BUILTIN_FIELDS));

  return (
    <div className="space-y-3">
      <datalist id={listId}>
        {Object.entries(fields).map(([name, f]) => (
          <option key={name} value={name}>{`${name} (${f.type})`}</option>
        ))}
      </datalist>

      <div>
        <div className="flex items-center justify-between gap-2">
          <span className={aaLabelCls}>
            Only when the event reports…
            {conditions.rules.length > 1 && (
              <select
                value={conditions.match}
                disabled={disabled}
                onChange={(e) => onChange({ conditions: { ...conditions, match: e.target.value as 'all' | 'any' } })}
                className="ml-1.5 rounded border border-border bg-background px-1 py-0.5 text-[11px] text-foreground"
                aria-label="Combine conditions"
              >
                <option value="all">all of these</option>
                <option value="any">any of these</option>
              </select>
            )}
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setRules([...conditions.rules, { field: firstNumeric, op: firstNumeric ? 'lt' : 'eq', value: '' }])}
            className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[12px] text-brand hover:bg-brand/10 disabled:opacity-50"
          >
            <PlusIcon className="h-3.5 w-3.5" /> Add condition
          </button>
        </div>
        {conditions.rules.length === 0 ? (
          <p className="mt-1 text-[11px] text-muted-foreground">
            No conditions — every matching event alerts. Add one to test a reading, e.g. value &lt; 150.
          </p>
        ) : (
          <div className="mt-1.5 space-y-1.5">
            {conditions.rules.map((r, i) => (
              <ConditionRow
                key={i}
                rule={r}
                index={i}
                fields={fields}
                listId={listId}
                disabled={disabled}
                onChange={(next) => setRules(conditions.rules.map((x, j) => (j === i ? next : x)))}
                onRemove={() => setRules(conditions.rules.filter((_, j) => j !== i))}
              />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={aaLabelCls}>…and it holds for (seconds)</span>
          <input
            type="number"
            min={0}
            max={86400}
            value={value.sustain_seconds ?? 0}
            disabled={disabled}
            onChange={(e) => onChange({ sustain_seconds: e.target.value === '' ? 0 : Number(e.target.value) })}
            className={aaInputCls}
          />
          <span className="mt-0.5 block text-[10px] text-muted-foreground">
            {hold > 0
              ? `Fires once it has held ${formatSeconds(hold)} — even if the model sends nothing more. Cleared by a matching event that breaks it.`
              : '0 = fire on the first matching event.'}
          </span>
        </label>
        {!hideMessage && <label className="block">
          <span className={aaLabelCls}>Alert message (optional)</span>
          <input
            value={value.message_template || ''}
            disabled={disabled}
            maxLength={500}
            onChange={(e) => onChange({ message_template: e.target.value })}
            className={aaInputCls}
            placeholder={firstNumeric ? `e.g. Reading {${firstNumeric}} for {sustained_s}s` : 'e.g. Belt stopped for {sustained_s}s'}
          />
          <span className="mt-0.5 block truncate text-[10px] text-muted-foreground" title={fieldNames.join(', ')}>
            {'{field}'} inserts a value{fieldNames.length ? `: ${fieldNames.slice(0, 5).map((f) => `{${f}}`).join(' ')}` : ''}
            {hold > 0 ? ' {sustained_s}' : ''}
          </span>
        </label>}
      </div>
    </div>
  );
}
